import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Undo2, Check } from "lucide-react";

const MAX_WIDTH = 1200;

// A high-resolution signing canvas. Backing store = cssWidth × max(dpr, 2) so the
// exported PNG is crisp enough (~300 DPI) for print / PDF. Pointer events so a
// finger or stylus works; strokes are kept for undo; the saved PNG is trimmed
// tight to the ink before export.
export default function SignatureCanvas({ onSave, saving }) {
  const canvasRef = useRef(null);
  const strokes = useRef([]); // [{ points:[{x,y}] }]
  const drawing = useRef(false);
  const scaleRef = useRef(2);
  const [hasInk, setHasInk] = useState(false);

  const cssSize = () => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { w: r.width, h: r.height };
  };

  const setup = () => {
    const c = canvasRef.current;
    if (!c) return;
    const scale = Math.max(window.devicePixelRatio || 1, 2);
    scaleRef.current = scale;
    const { w, h } = cssSize();
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    const ctx = c.getContext("2d");
    ctx.setTransform(scale, 0, 0, scale, 0, 0); // draw in CSS px; scale handles DPI + lineWidth
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    redraw();
  };

  useEffect(() => {
    setup();
    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redraw = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const { w, h } = cssSize();
    ctx.clearRect(0, 0, w, h);
    for (const s of strokes.current) {
      ctx.beginPath();
      s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    }
  };

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    strokes.current.push({ points: [pos(e)] });
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const s = strokes.current[strokes.current.length - 1];
    const p = pos(e);
    s.points.push(p);
    const ctx = canvasRef.current.getContext("2d");
    const prev = s.points[s.points.length - 2];
    ctx.beginPath();
    ctx.moveTo(prev.x, prev.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasInk(true);
  };
  const up = () => { drawing.current = false; };

  const clear = () => { strokes.current = []; redraw(); setHasInk(false); };
  const undo = () => { strokes.current.pop(); redraw(); setHasInk(strokes.current.length > 0); };

  // Trim transparent margins at full resolution, cap width at 1200, export PNG.
  const save = () => {
    if (!hasInk) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const { width: W, height: H } = c;
    const data = ctx.getImageData(0, 0, W, H).data;
    let minX = W, minY = H, maxX = 0, maxY = 0, found = false;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (data[(y * W + x) * 4 + 3] > 8) {
          found = true;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (!found) return;
    const pad = Math.round(6 * scaleRef.current);
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(W - 1, maxX + pad); maxY = Math.min(H - 1, maxY + pad);
    let cw = maxX - minX + 1;
    let ch = maxY - minY + 1;
    let outW = cw, outH = ch;
    if (outW > MAX_WIDTH) { const r = MAX_WIDTH / outW; outW = MAX_WIDTH; outH = Math.max(1, Math.round(ch * r)); }
    const out = document.createElement("canvas");
    out.width = outW; out.height = outH;
    out.getContext("2d").drawImage(c, minX, minY, cw, ch, 0, 0, outW, outH);
    onSave(out.toDataURL("image/png"));
  };

  return (
    <div data-testid="signature-canvas">
      <canvas
        ref={canvasRef}
        className="w-full h-40 rounded-lg bg-white border border-dashed border-zinc-400 touch-none cursor-crosshair"
        style={{ touchAction: "none" }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        onPointerCancel={up}
        data-testid="signature-canvas-surface"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-muted-foreground">Sign above with a finger, stylus or mouse</span>
        <div className="flex gap-1.5">
          <Button type="button" size="sm" variant="ghost" onClick={undo} disabled={!hasInk} data-testid="signature-undo"><Undo2 className="h-3.5 w-3.5 mr-1" />Undo</Button>
          <Button type="button" size="sm" variant="ghost" onClick={clear} disabled={!hasInk} data-testid="signature-clear"><Eraser className="h-3.5 w-3.5 mr-1" />Clear</Button>
          <Button type="button" size="sm" onClick={save} disabled={!hasInk || saving} data-testid="signature-save-drawn"><Check className="h-3.5 w-3.5 mr-1" />{saving ? "Saving…" : "Save signature"}</Button>
        </div>
      </div>
    </div>
  );
}
